/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, November 12th 2021, 12:03:03 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { APIMany2OneSelectFormInput, ConcreteAPIMany2OneSelectFormInputProps, HelperText } from "ui-widgets";
import { APISERVICEPATHS } from "lib/api";
import PostAddIcon from "@mui/icons-material/PostAdd";
import { DocumentTemplate } from "lib/schemas";
import { useOSFormContext } from "ui-widgets/forms/providers";
import { Edit as EditIcon } from "react-feather";
import IconButton from "@mui/material/IconButton";
import { COLORSCHEME } from "theme/styles";
import { resolveURLName } from "core/routes/utils";
import { useServiceBuilderContext } from "service-builder/providers";
import { useNavigate } from "react-router-dom";
import { UUID4 } from "lib/types";

export const DocumentTemplateMany2OnePicker = <TPrimaryObject,>(
  props: ConcreteAPIMany2OneSelectFormInputProps<TPrimaryObject, DocumentTemplate>
) => {
  const { documentTemplateId, navigateToDocumentTemplate, apiQueryParams } = useDocumentTemplateMany2OnePicker(
    props.schema_field_key
  );

  return (
    <>
      <APIMany2OneSelectFormInput<TPrimaryObject, DocumentTemplate>
        label={props.label ?? "Document Template"}
        itemIdProperty="id"
        getOptionLabel={(option) => option.title}
        loadingText="Loading available document templates"
        apiServicePath={APISERVICEPATHS.documentTemplates}
        apiQueryParams={apiQueryParams}
        inputStartAdornment={<PostAddIcon color="primary" />}
        inputEndAdornment={
          <DocumentEditButton
            documentTemplateId={documentTemplateId}
            navigateToDocumentTemplate={navigateToDocumentTemplate}
          />
        }
        {...props}
      />
      {props.helperText ? null : (
        <HelperText>
          With document templates you can configure system generated documents within your workflow such as letters,
          certificates, licenses and so forth. This comes with the ability to design and customize the document.
        </HelperText>
      )}
    </>
  );
};

const DocumentEditButton = ({ documentTemplateId, navigateToDocumentTemplate }) =>
  documentTemplateId ? (
    <IconButton onClick={navigateToDocumentTemplate}>
      <EditIcon color={COLORSCHEME.primary} />
    </IconButton>
  ) : null;

const useDocumentTemplateMany2OnePicker = <TPrimaryObject,>(
  schema_field_key: ConcreteAPIMany2OneSelectFormInputProps<TPrimaryObject, DocumentTemplate>["schema_field_key"]
) => {
  const { watch } = useOSFormContext<TPrimaryObject>();

  const documentTemplateId = watch<any>(schema_field_key);

  const {
    processDefinitionContext: { currentProcessDefinition },
  } = useServiceBuilderContext();

  const navigate = useNavigate();

  const navigateToDocumentTemplate = () =>
    navigate(
      resolveURLName("documentTemplate", {
        serviceId: currentProcessDefinition.id,
        documentTemplateId: documentTemplateId as UUID4,
      })
    );

  return {
    documentTemplateId,
    navigateToDocumentTemplate,
    apiQueryParams: { process_definition: currentProcessDefinition.id },
  };
};
